import { createContext, useCallback, useContext, useState, ReactNode } from "react";

export type CustomerType = "private" | "business";
const STORAGE_KEY = "dd_customer_type";

const readStored = (): CustomerType | null => {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "private" || v === "business" ? v : null;
  } catch {
    return null;
  }
};

interface Ctx {
  customerType: CustomerType;
  hasChosen: boolean;
  setCustomerType: (t: CustomerType) => void;
}

const CustomerTypeContext = createContext<Ctx | undefined>(undefined);

export const CustomerTypeProvider = ({ children }: { children: ReactNode }) => {
  const [stored, setStored] = useState<CustomerType | null>(readStored);

  const setCustomerType = useCallback((t: CustomerType) => {
    try { localStorage.setItem(STORAGE_KEY, t); } catch { /* ignore */ }
    setStored(t);
  }, []);

  return (
    <CustomerTypeContext.Provider value={{ customerType: stored ?? "private", hasChosen: stored !== null, setCustomerType }}>
      {children}
    </CustomerTypeContext.Provider>
  );
};

export const useCustomerType = () => {
  const ctx = useContext(CustomerTypeContext);
  if (!ctx) throw new Error("useCustomerType must be used within CustomerTypeProvider");
  return ctx;
};

export const VAT_LABEL: Record<CustomerType, Record<"sv" | "en" | "de", string>> = {
  private: { sv: "", en: "", de: "" },
  business: { sv: "exkl. moms", en: "excl. VAT", de: "zzgl. MwSt." },
};

export const CUSTOMER_TYPE_LABEL: Record<CustomerType, Record<"sv" | "en" | "de", string>> = {
  private: { sv: "Privatperson", en: "Private", de: "Privatkunde" },
  business: { sv: "Företag", en: "Business", de: "Geschäftskunde" },
};
